import { useState } from 'react';
import { Card, Divider, Tag } from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  BookOutlined,
  TrophyOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import type { ResumeMetadata } from '@/types/resume';

interface ResumePreviewProps {
  metadata?: Partial<ResumeMetadata>;
  /** 最大高度，设置后内容超出时内部滚动（如 "320px"、"40vh"），不设则不限制 */
  maxHeight?: string | number;
}

const ResumePreview = ({ metadata, maxHeight }: ResumePreviewProps) => {
  const [showFull, setShowFull] = useState(false);

  if (!metadata) {
    return null;
  }

  const { name, phone, email, workExperience, education, otherInfo, skills } = metadata;

  // 检查基本信息是否有值
  const hasBasicInfo = name || phone || email;

  // 检查工作经历是否有值
  const hasWorkExperience =
    workExperience &&
    workExperience.length > 0 &&
    workExperience.some(
      (exp) => exp?.company || exp?.position || exp?.startDate || exp?.endDate || exp?.description,
    );

  // 检查教育经历是否有值
  const hasEducation =
    education &&
    education.length > 0 &&
    education.some(
      (edu) => edu?.school || edu?.major || edu?.degree || edu?.startDate || edu?.endDate,
    );

  // 检查其他信息是否有值
  const hasOtherInfo =
    otherInfo &&
    (otherInfo.gender ||
      otherInfo.nativePlace ||
      otherInfo.politicalStatus ||
      otherInfo.englishLevel ||
      otherInfo.computerLevel ||
      otherInfo.currentResidence ||
      (otherInfo.awards && otherInfo.awards.length > 0) ||
      (otherInfo.certificates && otherInfo.certificates.length > 0));

  // 检查技能是否有值
  const hasSkills =
    skills &&
    ((skills.professionalSkills && skills.professionalSkills.length > 0) ||
      (skills.projectSkills && skills.projectSkills.length > 0));

  // 如果没有任何信息，返回 null
  if (!hasBasicInfo && !hasWorkExperience && !hasEducation && !hasOtherInfo && !hasSkills) {
    return null;
  }

  // 检查是否有更多内容需要展开显示
  const hasMoreContent = hasWorkExperience || hasEducation || hasOtherInfo || hasSkills;

  const content = (
    <div className="space-y-6">
        {/* 基本信息 */}
        {hasBasicInfo && (
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <UserOutlined className="text-indigo-500" />
              <span>基本信息</span>
            </h3>
            <div className="space-y-2 text-slate-700">
              {name && (
                <div className="flex items-center gap-2">
                  <UserOutlined className="text-slate-400" />
                  <span className="font-medium">姓名：</span>
                  <span>{name}</span>
                </div>
              )}
              {phone && (
                <div className="flex items-center gap-2">
                  <PhoneOutlined className="text-slate-400" />
                  <span className="font-medium">电话：</span>
                  <span>{phone}</span>
                </div>
              )}
              {email && (
                <div className="flex items-center gap-2">
                  <MailOutlined className="text-slate-400" />
                  <span className="font-medium">邮箱：</span>
                  <a className="text-indigo-600 hover:text-indigo-800">{email}</a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 展示更多按钮 */}
        {!showFull && hasMoreContent && (
          <div className="flex justify-center">
            <a onClick={() => setShowFull(true)} className="text-indigo-600 hover:text-indigo-800">
              展示更多
            </a>
          </div>
        )}

        {/* 工作经历 */}
        {showFull && hasWorkExperience && workExperience && workExperience.length > 0 && (
          <div>
            <Divider />
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <IdcardOutlined className="text-indigo-500" />
              <span>工作经历</span>
            </h3>
            <div className="space-y-4">
              {workExperience.map((exp, index) => {
                const hasExpData =
                  exp?.company ||
                  exp?.position ||
                  exp?.startDate ||
                  exp?.endDate ||
                  exp?.description;
                if (!hasExpData) return null;

                return (
                  <div key={index} className="pl-4 border-l-2 border-indigo-200">
                    <div className="space-y-2">
                      {(exp?.company || exp?.position) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {exp.position && (
                            <span className="font-semibold text-slate-900">{exp.position}</span>
                          )}
                          {exp.company && (
                            <>
                              {exp.position && <span className="text-slate-400">@</span>}
                              <span className="text-indigo-600">{exp.company}</span>
                            </>
                          )}
                        </div>
                      )}
                      {(exp?.startDate || exp?.endDate) && (
                        <div className="text-sm text-slate-500">
                          {exp.startDate && <span>{exp.startDate}</span>}
                          {exp.startDate && exp.endDate && <span> ~ </span>}
                          {exp.endDate && <span>{exp.endDate}</span>}
                        </div>
                      )}
                      {exp?.description && (
                        <div className="text-slate-700 mt-2 leading-relaxed">{exp.description}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 教育经历 */}
        {showFull && hasEducation && education && education.length > 0 && (
          <div>
            <Divider />
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <BookOutlined className="text-indigo-500" />
              <span>教育经历</span>
            </h3>
            <div className="space-y-4">
              {education.map((edu, index) => {
                const hasEduData =
                  edu?.school || edu?.major || edu?.degree || edu?.startDate || edu?.endDate;
                if (!hasEduData) return null;

                return (
                  <div key={index} className="pl-4 border-l-2 border-indigo-200">
                    <div className="space-y-2">
                      {(edu?.school || edu?.major || edu?.degree) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {edu.school && (
                            <span className="font-semibold text-slate-900">{edu.school}</span>
                          )}
                          {edu.major && (
                            <>
                              {edu.school && <span className="text-slate-400"> · </span>}
                              <span className="text-indigo-600">{edu.major}</span>
                            </>
                          )}
                          {edu.degree && (
                            <>
                              {(edu.school || edu.major) && (
                                <span className="text-slate-400"> · </span>
                              )}
                              <span className="text-slate-600">{edu.degree}</span>
                            </>
                          )}
                        </div>
                      )}
                      {(edu?.startDate || edu?.endDate) && (
                        <div className="text-sm text-slate-500">
                          {edu.startDate && <span>{edu.startDate}</span>}
                          {edu.startDate && edu.endDate && <span> ~ </span>}
                          {edu.endDate && <span>{edu.endDate}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 其他信息 */}
        {showFull && hasOtherInfo && otherInfo && (
          <div>
            <Divider />
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileTextOutlined className="text-indigo-500" />
              <span>其他信息</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-slate-700">
              {otherInfo.gender && (
                <div>
                  <span className="font-medium">性别：</span>
                  <span>{otherInfo.gender}</span>
                </div>
              )}
              {otherInfo.nativePlace && (
                <div>
                  <span className="font-medium">籍贯：</span>
                  <span>{otherInfo.nativePlace}</span>
                </div>
              )}
              {otherInfo.politicalStatus && (
                <div>
                  <span className="font-medium">政治面貌：</span>
                  <span>{otherInfo.politicalStatus}</span>
                </div>
              )}
              {otherInfo.englishLevel && (
                <div>
                  <span className="font-medium">英语水平：</span>
                  <span>{otherInfo.englishLevel}</span>
                </div>
              )}
              {otherInfo.computerLevel && (
                <div>
                  <span className="font-medium">计算机水平：</span>
                  <span>{otherInfo.computerLevel}</span>
                </div>
              )}
              {otherInfo.currentResidence && (
                <div className="flex items-center gap-2">
                  <EnvironmentOutlined className="text-slate-400" />
                  <span className="font-medium">现居地：</span>
                  <span>{otherInfo.currentResidence}</span>
                </div>
              )}
            </div>

            {/* 奖项 */}
            {otherInfo.awards && otherInfo.awards.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <TrophyOutlined className="text-amber-500" />
                  <span>奖项荣誉</span>
                </h4>
                <div className="space-y-2">
                  {otherInfo.awards.map((award, index) => (
                    <div key={index} className="text-slate-700 pl-6 relative">
                      <span className="absolute left-0 text-amber-500">•</span>
                      <span>{award}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 证书 */}
            {otherInfo.certificates && otherInfo.certificates.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileTextOutlined className="text-indigo-500" />
                  <span>证书资质</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {otherInfo.certificates.map((cert, index) => (
                    <Tag key={index} color="geekblue">
                      {cert}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 技能 */}
        {showFull && hasSkills && skills && (
          <div>
            <Divider />
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <GlobalOutlined className="text-indigo-500" />
              <span>技能特长</span>
            </h3>
            <div className="space-y-4">
              {skills.professionalSkills && skills.professionalSkills.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">专业技能</h4>
                  <div className="flex flex-wrap gap-2">
                    {skills.professionalSkills.map((skill, index) => (
                      <Tag key={index} color="cyan">
                        {skill}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
              {skills.projectSkills && skills.projectSkills.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">项目技能</h4>
                  <div className="flex flex-wrap gap-2">
                    {skills.projectSkills.map((skill, index) => (
                      <Tag key={index} color="purple">
                        {skill}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
  );

  const resolvedMaxHeight =
    maxHeight !== undefined
      ? typeof maxHeight === 'number'
        ? `${maxHeight}px`
        : maxHeight
      : undefined;

  return (
    <Card className="mt-4 shadow-sm">
      {resolvedMaxHeight ? (
        <div
          className="scrollbar-thin overflow-y-auto overflow-x-hidden rounded border-0"
          style={{ maxHeight: resolvedMaxHeight }}
        >
          {content}
        </div>
      ) : (
        content
      )}
    </Card>
  );
};

export default ResumePreview;
